import { FormAnswer } from 'components/registration/v2/PostRegEditDrawer';
import {
  areAnswersEqual,
  getFormQuestions,
  getQuestion,
  getQuestionDefaults,
  validateForm,
} from './helpers';
import {
  ActionType,
  HydrateFormPayload,
  LoadingType,
  PendingEdit,
  PostRegDrawerState,
  PreviewLineItems,
  SetConfirmationPayload,
  SetFormPagePayload,
  SetFormResultIdPayload,
  SetFormStatePayload,
  SetPendingEditsPayload,
  SetPendingPaymentPayload,
  SetPreviewLineItemsPayload,
  UpdateFormErrorPayload,
  UpdateFormFieldDetailsPayload,
  UpdateFormFieldFilePayload,
  UpdateFormFieldPayload,
} from './types';
import { defaultPostRegDrawerState } from './usePostRegEditDrawer';

export interface PostRegDrawerAction {
  type: ActionType;
  loading?: { loadingType: LoadingType; isLoading: boolean; error?: string };
  payload?:
    | SetFormStatePayload
    | HydrateFormPayload
    | UpdateFormFieldPayload
    | UpdateFormFieldDetailsPayload
    | SetFormResultIdPayload
    | UpdateFormErrorPayload
    | SetFormPagePayload
    | SetPendingPaymentPayload
    | SetPendingEditsPayload
    | SetPreviewLineItemsPayload
    | PreviewLineItems
    | PendingEdit[]
    | SetConfirmationPayload
    | UpdateFormFieldFilePayload;
}

export const postRegDrawerReducer = (
  state: PostRegDrawerState,
  action: PostRegDrawerAction
): PostRegDrawerState => {
  switch (action.type) {
    case ActionType.SetFormResultId: {
      const { formResultId, orderId } = action.payload as SetFormResultIdPayload;
      return { ...state, formResultId, orderId };
    }

    case ActionType.ResetState: {
      return {
        ...defaultPostRegDrawerState,
      };
    }

    case ActionType.SetLoading: {
      const { loadingType, isLoading, error } = action.loading;
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: isLoading, error },
      };
    }

    case ActionType.HydrateForm: {
      const { questions } = action.payload as HydrateFormPayload;
      const { registrantFirstName, registrantLastName, registrantEmail } =
        questions;
      const fullName = `${registrantFirstName} ${registrantLastName}`;
      const feeRelatedQuestions = getFormQuestions(
        questions.feeRelatedQuestions
      );
      const additionalQuestions = getFormQuestions(
        questions.additionalQuestions
      );
      const currentFormState = { ...state.currentFormState };

      // Add fee related questions to form values
      feeRelatedQuestions.forEach((question) => {
        currentFormState[question.id] = getQuestionDefaults(question);
      });
      // Add additional questions to form values
      additionalQuestions.forEach((question) => {
        currentFormState[question.id] = getQuestionDefaults(question);
      });

      const formStructure = {
        feeRelatedQuestions,
        additionalQuestions,
      };

      return {
        ...state,
        registrantInfo: { fullName, email: registrantEmail },
        formStructure,
        questions,
        currentFormState,
      };
    }

    case ActionType.UpdateFormField: {
      const { questionId, values } = action.payload as UpdateFormFieldPayload;
      const currentAnswers = state.currentFormState[questionId] || [];
      const updatedAnswers: FormAnswer[] = [];
      values.forEach((value) => {
        const existingValue = currentAnswers.find(
          (answer) => answer.value === value
        );
        if (existingValue) {
          updatedAnswers.push(existingValue);
        } else {
          updatedAnswers.push({ value });
        }
      });
      return updateFormField(state, questionId, updatedAnswers);
    }

    case ActionType.UpdateFormFieldDetails: {
      const { questionId, value, details } =
        action.payload as UpdateFormFieldDetailsPayload;
      const { currentFormState } = state;
      const currentAnswers = currentFormState[questionId] || [];
      const updatedAnswers = currentAnswers.map((answer) => {
        if (answer.value === value) {
          return { value, details };
        }
        return answer;
      });
      return updateFormField(state, questionId, updatedAnswers);
    }
    
    case ActionType.UpdateFormFieldFile: {
      const { questionId, value, file } =
        action.payload as UpdateFormFieldFilePayload;
      const { currentFormState } = state;
      const currentAnswers = currentFormState[questionId] || [];
      const updatedAnswers = currentAnswers.map((answer) => {
        if (answer.value === value) {
          return { ...answer, file };
        }
        return answer;
      });
      return updateFormField(state, questionId, updatedAnswers);
    }

    case ActionType.setFormPage: {
      const { formPage } = action.payload as SetFormPagePayload;
      return { ...state, formPage };
    }

    case ActionType.SetPreviewLineItems: {
      const { previewLineItems } = action.payload as SetPreviewLineItemsPayload;
      return { ...state, previewLineItems };
    }

    case ActionType.SetPendingPayment: {
      const { pendingPayment } = action.payload as SetPendingPaymentPayload;
      return { ...state, pendingPayment };
    }

    case ActionType.SetPendingEdits: {
      const { pendingEdits } = action.payload as SetPendingEditsPayload;
      return { ...state, pendingEdits };
    }

    case ActionType.SetConfirmation: {
      const { confirmation } = action.payload as SetConfirmationPayload;
      return { ...state, confirmation };
    }

    default:
      return state;
  }
};

// Helper function to update form field
function updateFormField(
  state: PostRegDrawerState,
  questionId: string,
  updatedAnswers: FormAnswer[]
) {
  const dirtyFields = [...state.dirtyFields];
  const updatedErrors = { ...state.formErrors };
  const newFormState = {
    ...state.currentFormState,
    [questionId]: updatedAnswers,
  };

  const isValid = validateForm(
    newFormState,
    state.formStructure,
    updatedErrors
  );

  // Update dirty fields
  const question = getQuestion(state.formStructure, questionId);
  const originalValue = getQuestionDefaults(question);

  const currentValue = updatedAnswers;
  const isDirty = !areAnswersEqual(originalValue, currentValue);

  if (isDirty && !dirtyFields.includes(questionId)) {
    dirtyFields.push(questionId);
  } else if (!isDirty && dirtyFields.includes(questionId)) {
    dirtyFields.splice(dirtyFields.indexOf(questionId), 1);
  }

  return {
    ...state,
    isFormValid: isValid,
    currentFormState: newFormState,
    formErrors: updatedErrors,
    dirtyFields,
  };
}
